import {
  SectionLabel,
  SummaryCard,
  Tags,
} from "@risalabs_frontend_org/oasis-ui-kit";
import type { SingleCoverageInformation } from "../../../../data-model/coverageInformation";
import {
  CoverageMiscellaneousAmountType,
  CoverageSubscriberType,
} from "../../../../enums/coverageInformationEnums";
import { CoverageNetworkDisplay } from "../../../../enums/coverageNetworkTypes";
import {
  ManualEvBvAmountType,
  ManualEvBvFieldKey,
  ManualEvBvSubscriberKind,
} from "../../../../enums/eventsData";
import type { ManualEvFieldEditedDetail } from "../../helpers/evCoverageAnalyticsEvents";
import {
  EvCoverageEditFieldId,
  evCoverageFieldShowsRequired,
  type EvCoverageFieldId,
} from "../../helpers/evCoverageEditValidation";
import {
  formatBvAmountFieldDisplay,
  formatBvFinancialDisplay,
} from "../../helpers/evCoverageFinancialDisplay";
import {
  bindNaOrNumericValueChange,
  bvAmountFieldEditValue,
  miscAmountEditValue,
} from "../../helpers/evCoverageFinancialInput";
import { EvCoverageFinancialLabeledValue } from "./evCoverageFinancialLabeledValue";

const INDIVIDUAL_BADGE = {
  backgroundColor: "#F3E8FF",
  borderColor: "#E9D5FF",
  textColor: "#6B21A8",
};

const FAMILY_BADGE = {
  backgroundColor: "#FEF9C3",
  borderColor: "#FDE047",
  textColor: "#854D0E",
};

const networkDisplayToLabel = (
  network: CoverageNetworkDisplay | undefined,
): string => {
  switch (network) {
    case CoverageNetworkDisplay.IN_NETWORK:
      return "In Network";
    case CoverageNetworkDisplay.OUT_NETWORK:
      return "Out of Network";
    case CoverageNetworkDisplay.NO_NETWORK:
      return "No Network";
    case CoverageNetworkDisplay.NOT_APPLICABLE:
      return "Not Applicable";
    case CoverageNetworkDisplay.UNKNOWN:
    default:
      return "Unknown";
  }
};

/**
 * Parses currency-like input from editable LabeledValue fields into a number or undefined.
 *
 * @param raw - User-entered text (may include `$`, commas, or legacy `NA`)
 * @returns Parsed finite number, or undefined when empty or not parseable
 */
const parseAmountInput = (raw: string): number | undefined => {
  const t = raw.replace(/\$/g, "").replace(/,/g, "").trim();
  if (t === "" || t.toUpperCase() === "NA" || t === "—" || t === "-")
    return undefined;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
};

export interface EvCoverageFinancialContentProps {
  singleCoverage: SingleCoverageInformation;
  activeTreatmentTab: string;
  isEditing?: boolean;
  onApplyCoverageEdit?: (
    recipe: (draft: SingleCoverageInformation) => void,
  ) => void;
  /** Debounced `manual_ev_field_edited` while BV financial fields change in edit mode. */
  onManualEvFieldEdited?: (detail: ManualEvFieldEditedDetail) => void;
  showFieldErrors?: boolean;
  invalidFieldIds?: ReadonlySet<EvCoverageFieldId>;
}

/** Deductibles, OOP, copay for the selected service category (tabs render in {@link EvCoverageUnifiedContent}). */
export const EvCoverageFinancialContent = (
  props: EvCoverageFinancialContentProps,
) => {
  const {
    singleCoverage,
    activeTreatmentTab,
    isEditing = false,
    onApplyCoverageEdit = () => {},
    onManualEvFieldEdited,
    showFieldErrors = false,
    invalidFieldIds = new Set<EvCoverageFieldId>(),
  } = props;

  const fieldRequired = (fieldId: EvCoverageFieldId) =>
    evCoverageFieldShowsRequired(showFieldErrors, invalidFieldIds, fieldId);

  const prEntries = singleCoverage.patientResponsibilityInformation ?? [];
  const parsedActiveTreatmentTab = Number.parseInt(activeTreatmentTab, 10);
  const safeActiveTreatmentTabIndex = Number.isNaN(parsedActiveTreatmentTab)
    ? undefined
    : parsedActiveTreatmentTab;

  const activePrIndex = Math.min(
    safeActiveTreatmentTabIndex ?? 0,
    Math.max(0, prEntries.length - 1),
  );
  const activePr = prEntries[activePrIndex]?.singlePatientResponsibility;

  const activeServiceTypeCode = String(
    activePr?.serviceTypeInformation?.serviceType ?? "",
  ).trim();

  const deductibles = activePr?.deductibleAmountInformation ?? [];
  const outOfPocket = activePr?.outOfPocketAmountInformation ?? [];
  const misc = activePr?.miscellaneousAmountInformation ?? [];

  const coPay = misc.find(
    (m) => m.type === CoverageMiscellaneousAmountType.CO_PAY,
  )?.amount;
  const coInsurance = misc.find(
    (m) => m.type === CoverageMiscellaneousAmountType.CO_INSURANCE,
  )?.amount;

  const individualDeductible = deductibles.find(
    (d) => d.subscriberType === CoverageSubscriberType.INDIVIDUAL,
  );
  const familyDeductible = deductibles.find(
    (d) => d.subscriberType === CoverageSubscriberType.FAMILY,
  );
  const individualOop = outOfPocket.find(
    (o) => o.subscriberType === CoverageSubscriberType.INDIVIDUAL,
  );
  const familyOop = outOfPocket.find(
    (o) => o.subscriberType === CoverageSubscriberType.FAMILY,
  );

  const notifyBvFieldEdited = (
    fieldKey: ManualEvBvFieldKey,
    valueEntered: string,
    options?: {
      amountType?: ManualEvBvAmountType;
      subscriber?: ManualEvBvSubscriberKind;
    },
  ) => {
    onManualEvFieldEdited?.({
      fieldCategory: "bv",
      fieldKey,
      serviceTypeCode: activeServiceTypeCode,
      valueEntered,
      ...options,
    });
  };

  const subscriberKindForAnalytics = (
    subscriber: CoverageSubscriberType,
  ): ManualEvBvSubscriberKind =>
    subscriber === CoverageSubscriberType.FAMILY
      ? ManualEvBvSubscriberKind.FAMILY
      : ManualEvBvSubscriberKind.INDIVIDUAL;

  const patchDed = (
    subscriber: CoverageSubscriberType,
    field: "totalAmount" | "usedAmount",
    raw: string,
  ) => {
    const nextVal = parseAmountInput(raw);
    const rawField =
      field === "totalAmount" ? "totalAmountRaw" : "usedAmountRaw";
    const trimmed = raw.trim();
    const nextRaw = trimmed === "" ? undefined : trimmed;
    onApplyCoverageEdit((draft) => {
      const pr =
        draft.patientResponsibilityInformation[activePrIndex]
          ?.singlePatientResponsibility;
      if (!pr) return;
      let row = pr.deductibleAmountInformation.find(
        (d) => d.subscriberType === subscriber,
      );
      if (!row) {
        row = { subscriberType: subscriber };
        pr.deductibleAmountInformation.push(row);
      }
      row[field] = nextVal;
      row[rawField] = nextRaw;
    });
  };

  const patchOop = (
    subscriber: CoverageSubscriberType,
    field: "totalAmount" | "usedAmount",
    raw: string,
  ) => {
    const nextVal = parseAmountInput(raw);
    const rawField =
      field === "totalAmount" ? "totalAmountRaw" : "usedAmountRaw";
    const trimmed = raw.trim();
    const nextRaw = trimmed === "" ? undefined : trimmed;
    onApplyCoverageEdit((draft) => {
      const pr =
        draft.patientResponsibilityInformation[activePrIndex]
          ?.singlePatientResponsibility;
      if (!pr) return;
      let row = pr.outOfPocketAmountInformation.find(
        (o) => o.subscriberType === subscriber,
      );
      if (!row) {
        row = { subscriberType: subscriber };
        pr.outOfPocketAmountInformation.push(row);
      }
      row[field] = nextVal;
      row[rawField] = nextRaw;
    });
  };

  const patchMisc = (type: CoverageMiscellaneousAmountType, raw: string) => {
    const trimmed = raw.trim();
    const amount =
      trimmed === "" ? "" : trimmed.toUpperCase() === "NA" ? "NA" : trimmed;
    onApplyCoverageEdit((draft) => {
      const pr =
        draft.patientResponsibilityInformation[activePrIndex]
          ?.singlePatientResponsibility;
      if (!pr) return;
      let row = pr.miscellaneousAmountInformation.find((m) => m.type === type);
      if (!row) {
        row = { type, amount };
        pr.miscellaneousAmountInformation.push(row);
      } else {
        row.amount = amount;
      }
    });
  };

  return (
    <div className="ev-coverage-financial flex min-w-0 flex-col gap-2 pl-0 sm:pl-0.5 md:gap-3 lg:gap-4">
      {!activePr && (
        <div className="text-small text-primaryGray-3">
          No treatment-level coverage details are available for this plan.
        </div>
      )}

      {activePr && (
        <div className="flex flex-col gap-3 md:gap-5">
          <div>
            <SectionLabel
              label="Deductibles"
              accentColor="#0056D6"
              className="mb-2 !text-tiny !font-bold !leading-tiny !tracking-[0.03125rem] !text-primaryGray-6 sm:mb-3"
            />
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3">
              <SummaryCard
                borderColor="#E5E7EB"
                className="!w-full"
                description={networkDisplayToLabel(
                  individualDeductible?.networkType,
                )}
                isEditable={isEditing}
                tag={
                  <Tags
                    label="Individual"
                    backgroundColor={INDIVIDUAL_BADGE.backgroundColor}
                    textColor={INDIVIDUAL_BADGE.textColor}
                    className="!text-x-tiny !font-bold !leading-4"
                  />
                }
              >
                <EvCoverageFinancialLabeledValue
                  label="Total"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          individualDeductible?.totalAmountRaw,
                          individualDeductible?.totalAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          individualDeductible?.totalAmountRaw,
                          individualDeductible?.totalAmount,
                        )
                  }
                  className="self-stretch"
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.IND_DED_TOTAL)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchDed(
                      CoverageSubscriberType.INDIVIDUAL,
                      "totalAmount",
                      v,
                    );
                    notifyBvFieldEdited(ManualEvBvFieldKey.DEDUCTIBLE, v, {
                      amountType: ManualEvBvAmountType.TOTAL,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.INDIVIDUAL,
                      ),
                    });
                  })}
                />
                <EvCoverageFinancialLabeledValue
                  label="Used"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          individualDeductible?.usedAmountRaw,
                          individualDeductible?.usedAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          individualDeductible?.usedAmountRaw,
                          individualDeductible?.usedAmount,
                        )
                  }
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.IND_DED_USED)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchDed(
                      CoverageSubscriberType.INDIVIDUAL,
                      "usedAmount",
                      v,
                    );
                    notifyBvFieldEdited(ManualEvBvFieldKey.DEDUCTIBLE, v, {
                      amountType: ManualEvBvAmountType.USED,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.INDIVIDUAL,
                      ),
                    });
                  })}
                />
              </SummaryCard>
              <SummaryCard
                borderColor="#E5E7EB"
                className="!w-full"
                description={networkDisplayToLabel(
                  familyDeductible?.networkType,
                )}
                isEditable={isEditing}
                tag={
                  <Tags
                    label="Family"
                    backgroundColor={FAMILY_BADGE.backgroundColor}
                    textColor={FAMILY_BADGE.textColor}
                    className="!text-x-tiny !font-bold !leading-4"
                  />
                }
              >
                <EvCoverageFinancialLabeledValue
                  label="Total"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          familyDeductible?.totalAmountRaw,
                          familyDeductible?.totalAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          familyDeductible?.totalAmountRaw,
                          familyDeductible?.totalAmount,
                        )
                  }
                  className="self-stretch"
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.FAM_DED_TOTAL)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchDed(CoverageSubscriberType.FAMILY, "totalAmount", v);
                    notifyBvFieldEdited(ManualEvBvFieldKey.DEDUCTIBLE, v, {
                      amountType: ManualEvBvAmountType.TOTAL,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.FAMILY,
                      ),
                    });
                  })}
                />
                <EvCoverageFinancialLabeledValue
                  label="Used"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          familyDeductible?.usedAmountRaw,
                          familyDeductible?.usedAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          familyDeductible?.usedAmountRaw,
                          familyDeductible?.usedAmount,
                        )
                  }
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.FAM_DED_USED)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchDed(CoverageSubscriberType.FAMILY, "usedAmount", v);
                    notifyBvFieldEdited(ManualEvBvFieldKey.DEDUCTIBLE, v, {
                      amountType: ManualEvBvAmountType.USED,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.FAMILY,
                      ),
                    });
                  })}
                />
              </SummaryCard>
            </div>
          </div>

          <div>
            <SectionLabel
              label="Out of Pocket"
              accentColor="#0056D6"
              className="mb-2 !text-tiny !font-bold !leading-tiny !tracking-[0.03125rem] !text-primaryGray-6 sm:mb-3"
            />
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3">
              <SummaryCard
                borderColor="#E5E7EB"
                className="!w-full"
                description={networkDisplayToLabel(individualOop?.networkType)}
                isEditable={isEditing}
                tag={
                  <Tags
                    label="Individual"
                    backgroundColor={INDIVIDUAL_BADGE.backgroundColor}
                    textColor={INDIVIDUAL_BADGE.textColor}
                    className="!text-x-tiny !font-bold !leading-4"
                  />
                }
              >
                <EvCoverageFinancialLabeledValue
                  label="Total"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          individualOop?.totalAmountRaw,
                          individualOop?.totalAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          individualOop?.totalAmountRaw,
                          individualOop?.totalAmount,
                        )
                  }
                  className="self-stretch"
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.IND_OOP_TOTAL)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchOop(
                      CoverageSubscriberType.INDIVIDUAL,
                      "totalAmount",
                      v,
                    );
                    notifyBvFieldEdited(ManualEvBvFieldKey.OUT_OF_POCKET, v, {
                      amountType: ManualEvBvAmountType.TOTAL,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.INDIVIDUAL,
                      ),
                    });
                  })}
                />
                <EvCoverageFinancialLabeledValue
                  label="Used"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          individualOop?.usedAmountRaw,
                          individualOop?.usedAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          individualOop?.usedAmountRaw,
                          individualOop?.usedAmount,
                        )
                  }
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.IND_OOP_USED)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchOop(
                      CoverageSubscriberType.INDIVIDUAL,
                      "usedAmount",
                      v,
                    );
                    notifyBvFieldEdited(ManualEvBvFieldKey.OUT_OF_POCKET, v, {
                      amountType: ManualEvBvAmountType.USED,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.INDIVIDUAL,
                      ),
                    });
                  })}
                />
              </SummaryCard>
              <SummaryCard
                borderColor="#E5E7EB"
                className="!w-full"
                description={networkDisplayToLabel(familyOop?.networkType)}
                isEditable={isEditing}
                tag={
                  <Tags
                    label="Family"
                    backgroundColor={FAMILY_BADGE.backgroundColor}
                    textColor={FAMILY_BADGE.textColor}
                    className="!text-x-tiny !font-bold !leading-4"
                  />
                }
              >
                <EvCoverageFinancialLabeledValue
                  label="Total"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          familyOop?.totalAmountRaw,
                          familyOop?.totalAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          familyOop?.totalAmountRaw,
                          familyOop?.totalAmount,
                        )
                  }
                  className="self-stretch"
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.FAM_OOP_TOTAL)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchOop(CoverageSubscriberType.FAMILY, "totalAmount", v);
                    notifyBvFieldEdited(ManualEvBvFieldKey.OUT_OF_POCKET, v, {
                      amountType: ManualEvBvAmountType.TOTAL,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.FAMILY,
                      ),
                    });
                  })}
                />
                <EvCoverageFinancialLabeledValue
                  label="Used"
                  value={
                    isEditing
                      ? bvAmountFieldEditValue(
                          familyOop?.usedAmountRaw,
                          familyOop?.usedAmount,
                        )
                      : formatBvAmountFieldDisplay(
                          familyOop?.usedAmountRaw,
                          familyOop?.usedAmount,
                        )
                  }
                  isEditable={isEditing}
                  editAs="text"
                  required={fieldRequired(EvCoverageEditFieldId.FAM_OOP_USED)}
                  onValueChange={bindNaOrNumericValueChange((v) => {
                    patchOop(CoverageSubscriberType.FAMILY, "usedAmount", v);
                    notifyBvFieldEdited(ManualEvBvFieldKey.OUT_OF_POCKET, v, {
                      amountType: ManualEvBvAmountType.USED,
                      subscriber: subscriberKindForAnalytics(
                        CoverageSubscriberType.FAMILY,
                      ),
                    });
                  })}
                />
              </SummaryCard>
            </div>
          </div>
          <div className="grid h-[2.625rem] shrink-0 grid-cols-2 grid-rows-1 gap-x-4 gap-y-4 self-stretch sm:gap-x-6 md:gap-x-8">
            <div className="col-span-1 col-start-1 row-span-1 row-start-1 self-stretch justify-self-stretch">
              <EvCoverageFinancialLabeledValue
                label="Copay"
                value={
                  isEditing
                    ? miscAmountEditValue(coPay)
                    : formatBvFinancialDisplay(coPay, "money")
                }
                className="self-stretch"
                isEditable={isEditing}
                editAs="text"
                required={fieldRequired(EvCoverageEditFieldId.CO_PAY)}
                onValueChange={bindNaOrNumericValueChange((v) => {
                  patchMisc(CoverageMiscellaneousAmountType.CO_PAY, v);
                  notifyBvFieldEdited(ManualEvBvFieldKey.COPAY, v);
                })}
              />
            </div>
            <div className="col-span-1 col-start-2 row-span-1 row-start-1 self-stretch justify-self-stretch">
              <EvCoverageFinancialLabeledValue
                label="Co-Insurance"
                value={
                  isEditing
                    ? miscAmountEditValue(coInsurance)
                    : formatBvFinancialDisplay(coInsurance, "percent")
                }
                className="self-stretch"
                isEditable={isEditing}
                editAs="text"
                required={fieldRequired(EvCoverageEditFieldId.CO_INSURANCE)}
                onValueChange={bindNaOrNumericValueChange((v) => {
                  patchMisc(CoverageMiscellaneousAmountType.CO_INSURANCE, v);
                  notifyBvFieldEdited(ManualEvBvFieldKey.CO_INSURANCE, v);
                })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvCoverageFinancialContent;
